import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Formik } from "formik";
import * as Styles from "@material-ui/core/styles";
import * as Core from "@material-ui/core";
import * as YUP from "yup";
import * as Icons from "@material-ui/icons";

import TopBar from "../../../layouts/DashboardLayout/TopBar";
import {
  SaveWorkerDemanderForm,
  UpdateWorkRequestForm,
} from "src/redux/actions/workerdemander";

import ClientInfoByGuest from "./ClientInfoByGuest";
import ClientSelectByAdmin from "./ClientSelectByAdmin";
import WorkInformation from "./WorkInformation";
import WorkerSelection from "./WorkerSelection";
import WorkLocation from "./WorkLocation";
import { useParams, useNavigate } from "react-router-dom";
import Information from "src/components/Information";
import { ManagementService, WorkerDemanderService } from "src/services";

const useQontoStepIconStyles = Styles.makeStyles({
  root: { color: "#eaeaf0", display: "flex", height: 22, alignItems: "center" },
  active: { color: "#784af4" },
  circle: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
  completed: { color: "#784af4", zIndex: 1, fontSize: 18 },
});

function QontoStepIcon(props) {
  const classes = useQontoStepIconStyles();
  const { active, completed } = props;

  return (
    <div className={clsx(classes.root, { [classes.active]: active })}>
      {completed ? (
        <Icons.Check className={classes.completed} />
      ) : (
        <div className={classes.circle} />
      )}
    </div>
  );
}

QontoStepIcon.propTypes = {
  active: PropTypes.bool, // Whether this step is active.
  completed: PropTypes.bool, // Mark the step as completed. Is passed to child components.
};

const ColorlibConnector = Styles.withStyles({
  alternativeLabel: { top: 22 },
  active: {
    "& $line": {
      backgroundImage:
        "linear-gradient( 95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)",
    },
  },
  completed: {
    "& $line": {
      backgroundImage:
        "linear-gradient( 95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)",
    },
  },
  line: { height: 3, border: 0, backgroundColor: "#eaeaf0", borderRadius: 1 },
})(Core.StepConnector);

const useColorlibStepIconStyles = Styles.makeStyles({
  root: {
    backgroundColor: "#ccc",
    zIndex: 1,
    color: "#fff",
    width: 50,
    height: 50,
    display: "flex",
    borderRadius: "50%",
    justifyContent: "center",
    alignItems: "center",
  },
  active: {
    backgroundImage:
      "linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)",
    boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
  },
  completed: {
    backgroundImage:
      "linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)",
  },
});

function ColorlibStepIcon(props) {
  const classes = useColorlibStepIconStyles();
  const { active, completed } = props;

  const icons = {
    1: <Icons.Person />,
    2: <Icons.Work />,
    3: <Icons.People />,
    4: <Icons.MapSharp />,
  };

  return (
    <div
      className={clsx(classes.root, {
        [classes.active]: active,
        [classes.completed]: completed,
      })}
    >
      {icons[String(props.icon)]}
    </div>
  );
}

ColorlibStepIcon.propTypes = {
  active: PropTypes.bool, // Whether this step is active.
  completed: PropTypes.bool, // Mark the step as completed. Is passed to child components.
  icon: PropTypes.node, // The label displayed in the step icon.
};

const useStyles = Styles.makeStyles((theme) => ({
  root: { width: "100%" },
  button: { marginRight: theme.spacing(1) },
  instructions: { marginTop: theme.spacing(1), marginBottom: theme.spacing(1) },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
  },
}));

function getSteps(t, role) {
  if (role === "Admin" || role === "Accountant") {
    return [
      t("pages.workrequest.clientSelectByAdmin.stepname"),
      t("pages.workrequest.workInformation.stepname"),
      t("pages.workrequest.workerSelection.stepname"),
      t("pages.workrequest.workLocation.stepname"),
    ];
  } else if (role === "Client") {
    return [
      t("pages.workrequest.workInformation.stepname"),
      t("pages.workrequest.workerSelection.stepname"),
      t("pages.workrequest.workLocation.stepname"),
    ];
  } else {
    return [
      t("pages.workrequest.clientInfoByGuest.stepname"),
      t("pages.workrequest.workInformation.stepname"),
      t("pages.workrequest.workerSelection.stepname"),
      t("pages.workrequest.workLocation.stepname"),
    ];
  }
}

function getStepContent(
  step,
  touched,
  errors,
  values,
  handleBlur,
  handleChange,
  setFieldValue,
  role,
  setWorkerDemanderId,
  clients,
  countryList,
  getResult,
  cityList,
  tagList
) {
  if (role === "Admin" || role === "Accountant") {
    switch (step) {
      case 0:
        return (
          <ClientSelectByAdmin
            touched={touched}
            errors={errors}
            values={values}
            handleBlur={handleBlur}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            role={role}
            setWorkerDemanderId={setWorkerDemanderId}
            clients={clients}
          />
        );
      case 1:
        return (
          <WorkInformation
            touched={touched}
            errors={errors}
            values={values}
            handleBlur={handleBlur}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            role={role}
          />
        );
      case 2:
        return (
          <WorkerSelection
            touched={touched}
            errors={errors}
            values={values}
            handleBlur={handleBlur}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            role={role}
            tagList={tagList}
          />
        );
      case 3:
        return (
          <WorkLocation
            touched={touched}
            errors={errors}
            values={values}
            handleBlur={handleBlur}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            role={role}
            countryList={countryList}
            getResult={getResult}
            cityList={cityList}
          />
        );
      default:
        return "Unknown step";
    }
  } else if (role === "Client") {
    switch (step) {
      case 0:
        return (
          <WorkInformation
            touched={touched}
            errors={errors}
            values={values}
            handleBlur={handleBlur}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            role={role}
          />
        );
      case 1:
        return (
          <WorkerSelection
            touched={touched}
            errors={errors}
            values={values}
            handleBlur={handleBlur}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            role={role}
            tagList={tagList}
          />
        );
      case 2:
        return (
          <WorkLocation
            touched={touched}
            errors={errors}
            values={values}
            handleBlur={handleBlur}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            role={role}
            countryList={countryList}
            getResult={getResult}
            cityList={cityList}
          />
        );
      default:
        return "Unknown step";
    }
  } else {
    switch (step) {
      case 0:
        return (
          <ClientInfoByGuest
            touched={touched}
            errors={errors}
            values={values}
            handleBlur={handleBlur}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            role={role}
          />
        );
      case 1:
        return (
          <WorkInformation
            touched={touched}
            errors={errors}
            values={values}
            handleBlur={handleBlur}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            role={role}
          />
        );
      case 2:
        return (
          <WorkerSelection
            touched={touched}
            errors={errors}
            values={values}
            handleBlur={handleBlur}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            role={role}
            tagList={tagList}
          />
        );
      case 3:
        return (
          <WorkLocation
            touched={touched}
            errors={errors}
            values={values}
            handleBlur={handleBlur}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            role={role}
            countryList={countryList}
            getResult={getResult}
            cityList={cityList}
          />
        );
      default:
        return "Unknown step";
    }
  }
}

const WorkRequest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formikRef = useRef();
  const classes = useStyles();
  const { role } = useSelector((state) => state.authReducer);
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = React.useState(0);
  const [processStatus, setProcessStatus] = React.useState(false);
  const [workerDemanderId, setWorkerDemanderId] = React.useState({
    label: "",
    value: "",
  });
  const [clients, setClients] = React.useState([]);
  const [countryList, setCountryList] = React.useState([]);
  const [cityList, setCityList] = React.useState([]);
  const [tagList, setTagList] = React.useState([]);
  const steps = getSteps(t, role);
  const { id } = useParams();
  const isAddMode = !id;

  const initialValues = {
    workerDemanderId: 0,
    workerDemanderName: "",
    workerDemanderDetails: {
      logoPath: "",
      firmName: "",
      firstName: "",
      lastName: "",
      initials: "",
      privatePhoneNumber: "",
      mobilePhoneNumber: "",
      emailAddress: "",
      currentAccountCardCode: "",
      currentAccountCardCodeDescription: "",
      paymentMethodCode: "",
      taxNumber: "",
      registrationNumber: "",
      debtorNumber: "",
      chamberOfCommerceNumber: "",
      oneAddressSave: {
        label: "",
        detail: "",
        postCode: "",
        city: "",
        countryCode: "",
        isPrimary: true,
      },
      addressList: [],  
    },
    title: "",
    beginDate: "",
    endDate: "",
    shiftCount: 0,
    workDescription: "",
    selectedAddressId: 0,
    selectedAddressName: 0,
    selectedAddress: {
      label: "",
      detail: "",
      postCode: "",
      city: "",
      countryCode: "",
      isPrimary: true,
    },
    isCancelled: true,
    cancellationDate: null,
    isPlanned: true,
    isRegistered: false,
    neededWorkerList: [],
  };
  const validationSchema = YUP.object({});

  const { success, error } = useSelector(
    (state) => state.workerdemanderReducer
  );

  if (success && processStatus && activeStep === steps.length - 1) {
    setProcessStatus(false);
    if (role === "Admin") {
      navigate("/app/worker_demander_management", { replace: true });
    } else if (role === "Client") {
      navigate("/app/", { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  }

  const handleNext = () => {
    if (activeStep != steps.length - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  async function createWorkerDemander(values, setSubmitting) {
    dispatch(SaveWorkerDemanderForm(values, setSubmitting, setProcessStatus));
    await new Promise((resolve) => setTimeout(resolve, 2000));
  }

  async function updateWorkRequest(id, values, setSubmitting) {
    dispatch(
      UpdateWorkRequestForm(id, values, setSubmitting, setProcessStatus)
    );
    await new Promise((resolve) => setTimeout(resolve, 2000));
  }
  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    if (activeStep === steps.length - 1) {
      if (isAddMode) {
        await createWorkerDemander(values, setSubmitting);
      } else {
        await updateWorkRequest(id, values, setSubmitting);
      }
    } else {
      setSubmitting(false);
      handleNext();
    }
  };

  const getTagList = async () => {
    var dataX = [];
    const response = await ManagementService.GET_STRINGS_VALUES(
      "VocationalTag"
    );
    const { status, statusText } = response;
    if (status == 200) {
      const { success, message, data } = await response.data;
      if (success) {
        data.forEach((rc) => {
          dataX.push({
            value: rc.id,
            label: rc.stringValue,
          });
        });
        setTagList(dataX);
      } else {
        console.error(message);
      }
    } else {
      console.error(status, statusText);
    }
  };
  const getClientList = async () => {
    try {
      let clientList = [];
      const response = await WorkerDemanderService.GET_WORKER_DEMANDERS_ASYNC();
      const { success, message } = await response.data;

      if (success) {
        const { data } = await response.data;
        if (data) {
          data.forEach((rc) => {
            clientList.push({
              value: rc.id,
              label: rc.firmName + " - " + rc.firstName + " " + rc.lastName,
            });
          });

          setClients(clientList);
        }
      } else {
        console.error(message);
      }
    } catch (error) {
      const { message, response } = error;
      if (response) {
        const { data } = response;
        if (data) {
          console.error(JSON.stringify(data));
        }
      } else {
        console.error(JSON.stringify(message));
      }
    }
  };

  const getWorkerDemanderById = async () => {
    try {
      const response = await WorkerDemanderService.GET_WORK_REQUEST_ASYNC(id);

      const { success, message, data } = await response.data;

      if (success) {
        await getResult(data.selectedAddress.countryCode + "Cities");
        var generatedObject = {
          ...data,
          workerDemanderName:
            data.workerDemanderDetails?.firmName +
            " - " +
            data.workerDemanderDetails?.firstName +
            " " +
            data.workerDemanderDetails.lastName,
          selectedAddressName:
            data.selectedAddress?.countryCode +
            "," +
            data.selectedAddress?.city +
            "," +
            data.selectedAddress?.postCode +
            "," +
            data.selectedAddress?.detail,
        };
        if (formikRef.current) {
          await formikRef.current.setValues(generatedObject);
        }
      } else {
        console.log("Error (GET_WORKER_DEMANDER_EXTRA_BY_ID) :  ", message);
      }
    } catch (error) {
      console.log("Error (GET_WORKER_DEMANDER_EXTRA_BY_ID) :  ", error);
    }
  };

  const getResult = async (metadataGroup) => {
    var metaList = [{ metadataCode: "", metadataValue: "Select" }];
    const result = await ManagementService.GET_METEDATA_VALUES(metadataGroup);
    const { data } = await result.data;
    if (data) {
      data.forEach((rc) => {
        metaList.push({
          metadataCode: rc.metadataCode,
          metadataValue: rc.metadataValue,
        });
      });
    }
    if (metadataGroup === "Country") {
      console.log("cihan", metaList);
      setCountryList(metaList);
    } else {
      setCityList(metaList);
    }
  };
  const getParameters = async () => {
    await getResult("Country");
  };

  const callllAPIS = async () => {
    await getParameters();
    await getTagList();

    if (role === "Admin" || role === "Accountant") {
      await getClientList();
    }
    if (!isAddMode) {
      await getWorkerDemanderById();
    }
  };
  useEffect(() => {
    callllAPIS();
  }, []);
  return (
    <Core.Container>
      <TopBar />

      <div
        style={{
          position: "fixed",
          top: 60,
          left: "10%",
          width: "80%",
          zIndex: 1,
        }}
      >
        <Core.Stepper
          alternativeLabel
          activeStep={activeStep}
          connector={<ColorlibConnector />}
        >
          {steps.map((label) => (
            <Core.Step key={label}>
              <Core.StepLabel StepIconComponent={ColorlibStepIcon}>
                {label}
              </Core.StepLabel>
            </Core.Step>
          ))}
        </Core.Stepper>
      </div>

      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          setFieldValue,
          isValid,
          isSubmitting,
        }) => (
          <>
            <form onSubmit={handleSubmit}>
              <div style={{ paddingTop: 150, paddingBottom: 75 }}>
                {getStepContent(
                  activeStep,
                  touched,
                  errors,
                  values,
                  handleBlur,
                  handleChange,
                  setFieldValue,
                  role,
                  setWorkerDemanderId,
                  clients,
                  countryList,
                  getResult,
                  cityList,
                  tagList
                )}
              </div>

              <div
                style={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  paddingBottom: 20,
                  backgroundColor: "white",
                  zIndex: 1,
                }}
              >
                <Core.Grid item md={12} xs={12} align="center">
                  <Core.Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    className={classes.button}
                  >
                    {t("common.back")}
                  </Core.Button>
                  <Core.Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={activeStep === steps.length - 1 && isSubmitting}
                    className={classes.button}
                  >
                    {activeStep === steps.length - 1
                      ? t("common.finish")
                      : t("common.next")}
                  </Core.Button>
                  {activeStep === steps.length - 1 && isSubmitting ? (
                    <Core.CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  ) : null}
                </Core.Grid>
              </div>
            </form>
            {(success !== null || success !== undefined) &&
            !isSubmitting &&
            processStatus ? (
              <Information
                success={success}
                message={success ? "Saved Successfuly" : error}
              />
            ) : null}
            <pre>{JSON.stringify(values, null, 2)}</pre>
          </>
        )}
      </Formik>
    </Core.Container>
  );
};

export default WorkRequest;
